/** @jsx jsx */
import { jsx } from "@emotion/react";
import noop from "lodash/fp/noop";
import { FC } from "react";
import { useIntl } from "react-intl";
import AdminShortcutNav from "src/components/molecules/AdminShortcutNav";
import {
  asyncNoop,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  EMPTY_SET,
} from "../../../../globalConsts";

import {
  AnyIndexDictionary,
  Order,
  OrderValue,
  StringIndexedDictionary,
  TemplateId,
} from "../../../../globalTypes";
import {
  IndicesOfSubmittingInsertRows,
  InsertRows,
  OnInsertCellBlur,
  RemoveInsertRow,
  SubmitInsertRow,
  UpdateTableInsertCell,
} from "../../../../hooks/table/useInsertRows";
import {
  RowKey,
  TableCellValue,
  TableColumns,
  TableRow,
} from "../../../../requests/table/types";
import { EditRowWithColumns } from "../../../../requests/tableRowActions/types";
import { ColumnFormatDict } from "../../../../requests/templates/types";
import { getOnlyVisibleRowCells } from "../../../../utils/table";
import FlowioErrorBoundary from "../../../atoms/FlowioErrorBoundary";
import Loader, { withLoaderHOC } from "../../../atoms/Loader";
import { SelectedRowKeys } from "../../TemplateView/fragments/TemplateTable/types";
import {
  ColumnLabelOverrides,
  CustomColumnsGenerator,
  ITableRowControls,
} from "../types";
import {
  FlowioDivTable,
  FlowioDivTableBody,
  FlowioDivTableOverflow,
  FlowioTableWrapper,
  noRowsMessageCss,
} from "./styles";
import TableDataHead from "./TableDataHead";
import TableDataRow from "./TableDataRow";
import { sortColumns } from "./utils";

const TableDataDesktop: FC<TableDataDesktopProps> = ({
  templateId,
  rows,
  columns,
  inlineRowEditProps,
  columnFormatDict = EMPTY_OBJECT,
  allowRowSelection = false,
  isDataLoading,
  toggleSelectedStatusForRow = noop,
  toggleSelectedStatusForAllRowsOnCurrentPage = noop,
  areAllRowsOnCurrentPageAlreadySelected = false,
  keysOfSelectedRows = EMPTY_SET,
  order,
  onOrder,
  TableRowDesktopControls,
  customColumnsToStart,
  customColumnsToEnd,
  insertRows = EMPTY_ARRAY,
  onInsertCellEdit = noop,
  onInsertCellBlur,
  submitInsertRow = asyncNoop,
  removeInsertRow = noop,
  indicesOfSubmittingInsertRows = EMPTY_ARRAY,
  allowRowClick = false,
  onRowClickCallback = noop,
  refetchRowCallback = noop,
  columnLabelOverrides,
  columnOrderByColumnName = {},
  editedColumnOrderByColumnName = {},
}) => {
  const {
    editRows = EMPTY_OBJECT,
    onCellBlur,
    onCellEdit = noop,
    editColumns = EMPTY_OBJECT,
    submitEditRow = noop,
    resetEditRow = noop,
    hashesOfRowsBeingEdited,
    toggleRowEditMode = noop,
    hashesOfSubmittingEditRows = EMPTY_ARRAY,
    setEditableRowSubmit = noop,
  } = inlineRowEditProps ?? {};

  const { formatMessage } = useIntl();

  if (isDataLoading) return <Loader />;

  if (!rows.length && !insertRows?.length) {
    return (
      <div css={noRowsMessageCss}>{formatMessage({ id: "table.no_rows" })}</div>
    );
  }

  const sortedColumns = sortColumns(columns, columnOrderByColumnName);
  const sortedEditColumns = sortColumns(
    editColumns ?? {},
    editedColumnOrderByColumnName
  );

  if (rows.length === 1) {
    onRowClickCallback({
      row: getOnlyVisibleRowCells(rows[0], sortedColumns),
      columns: sortedColumns,
    });
  }

  return (
    <FlowioTableWrapper data-wizard="tables" hideBoxShadow data-cy="table">
      <FlowioDivTableOverflow>
        <FlowioDivTable>
          <TableDataHead
            allowRowSelection={allowRowSelection}
            columns={sortedColumns}
            columnFormatDict={columnFormatDict}
            order={order}
            onOrder={onOrder}
            toggleSelectedStatusForAllRowsOnCurrentPage={
              toggleSelectedStatusForAllRowsOnCurrentPage
            }
            areAllRowsOnCurrentPageAlreadySelected={
              areAllRowsOnCurrentPageAlreadySelected
            }
            customColumnsToStart={
              customColumnsToStart && customColumnsToStart(rows[0])
            }
            customColumnsToEnd={
              customColumnsToEnd && customColumnsToEnd(rows[0])
            }
            columnLabelOverrides={columnLabelOverrides}
          />
          <FlowioDivTableBody data-cy="rows">
            <FlowioErrorBoundary>
              {insertRows.map((row, index) => {
                const rowWithOnlyVisibleCells = getOnlyVisibleRowCells(
                  row as TableRow,
                  sortedColumns
                );

                return (
                  <TableDataRow
                    row={row}
                    key={`${row?.hash}-${String(index)}`}
                    columns={sortedEditColumns}
                    columnFormatDict={columnFormatDict}
                    allowRowSelection={allowRowSelection}
                    onRowClickCallback={() =>
                      onRowClickCallback({
                        row: rowWithOnlyVisibleCells,
                        columns: sortedColumns,
                      })
                    }
                    customColumnsToStart={customColumnsToStart}
                    editMode
                    isLoading={indicesOfSubmittingInsertRows.includes(index)}
                    onEditRowReset={() => removeInsertRow(index)}
                    updateEditRow={onInsertCellEdit.bind(null, index)}
                    onCellBlur={() => onInsertCellBlur?.(index)}
                    TableRowDesktopControls={TableRowDesktopControls}
                    onSubmit={() => submitInsertRow(index)}
                    columnOrderByColumnName={editedColumnOrderByColumnName}
                  />
                );
              })}
              {rows.map((row, index) => {
                const { hash: rowHash, keyValue: rowKey } = row;

                const editMode = hashesOfRowsBeingEdited?.includes(rowHash);

                const rowToUse = editMode ? editRows[rowHash] : row;
                const columnsToUse = editMode
                  ? sortedEditColumns
                  : sortedColumns;
                const columnsOrderedByNameForEditMode = editMode
                  ? editedColumnOrderByColumnName
                  : columnOrderByColumnName;

                const isEditRowLoading = editMode && !editRows[rowHash];
                const isEditRowSubmitting =
                  hashesOfSubmittingEditRows?.includes(rowHash);

                // protože nejde najít konkrétní rowHash v hashesOfSubmittingEditRows (hash před zmáčknutím edit je jiný než po submitu, read only row je jiná než editable row)
                const isAnyRowSubmitting = !!hashesOfSubmittingEditRows?.length;

                const rowWithOnlyVisibleCells = getOnlyVisibleRowCells(
                  rowToUse,
                  sortedColumns
                );

                return (
                  <TableDataRow
                    key={`${rowHash}-${String(index)}`}
                    row={rowToUse}
                    columns={columnsToUse}
                    onEditRowReset={() => resetEditRow(rowHash)}
                    editMode={editMode}
                    updateEditRow={onCellEdit.bind(null, rowHash)}
                    onCellBlur={() => onCellBlur?.(rowHash)}
                    isLoading={isEditRowSubmitting || isEditRowLoading}
                    toggleEditMode={() => toggleRowEditMode(rowHash)}
                    columnFormatDict={columnFormatDict}
                    TableRowDesktopControls={TableRowDesktopControls}
                    allowRowSelection={allowRowSelection}
                    allowRowClick={allowRowClick}
                    onRowSelection={() => toggleSelectedStatusForRow(rowKey)}
                    onRowClickCallback={() =>
                      onRowClickCallback({
                        columns: sortedColumns,
                        row: rowWithOnlyVisibleCells,
                      })
                    }
                    isSelected={keysOfSelectedRows.has(rowKey)}
                    customColumnsToStart={customColumnsToStart}
                    customColumnsToEnd={customColumnsToEnd}
                    onSubmit={() => submitEditRow(rowHash)}
                    refetchRowCallback={() => refetchRowCallback(rowHash)}
                    columnOrderByColumnName={columnsOrderedByNameForEditMode}
                  />
                );
              })}
            </FlowioErrorBoundary>
          </FlowioDivTableBody>
        </FlowioDivTable>
      </FlowioDivTableOverflow>
      {!!templateId && <AdminShortcutNav templateId={templateId} />}
    </FlowioTableWrapper>
  );
};

export type UpdateTableCell = (
  rowHash: string,
  column: string,
  value: TableCellValue
) => void;

export type InlineRowEditPropset = {
  editRows?: AnyIndexDictionary<TableRow>;
  onCellBlur?(rowHash: string): void;
  onCellEdit?: UpdateTableCell;
  submitEditRow?: (rowHash: string) => void;
  resetEditRow?: (rowHash: string) => void;
  editColumns?: TableColumns;
  hashesOfSubmittingEditRows?: string[];
  hashesOfRowsBeingEdited?: string[];
  toggleRowEditMode?: (rowHash: string) => void;
  fetchEditRow?: (rowHash: string) => Promise<void>;
  setEditableRowSubmit?: (rowHash: string) => void;
};

export interface TableDataDesktopProps {
  templateId?: TemplateId;
  rows: TableRow[];
  columns: TableColumns;
  TableRowDesktopControls?: ITableRowControls | null;
  columnFormatDict?: ColumnFormatDict;
  columnOrderByColumnName?: StringIndexedDictionary<number>;
  editedColumnOrderByColumnName?: StringIndexedDictionary<number>;
  inlineRowEditProps?: InlineRowEditPropset;
  isDataLoading?: boolean;
  insertRows?: InsertRows;
  onInsertCellEdit?: UpdateTableInsertCell;
  onInsertCellBlur?: OnInsertCellBlur;
  submitInsertRow?: SubmitInsertRow;
  removeInsertRow?: RemoveInsertRow;
  indicesOfSubmittingInsertRows?: IndicesOfSubmittingInsertRows;

  allowRowSelection?: boolean;
  keysOfSelectedRows?: SelectedRowKeys;
  toggleSelectedStatusForRow?: (key: RowKey) => void;
  toggleSelectedStatusForAllRowsOnCurrentPage?: () => void;
  areAllRowsOnCurrentPageAlreadySelected?: boolean;

  allowRowClick?: boolean;
  onRowClickCallback?: (tableRow: EditRowWithColumns) => void;

  order: Order;
  onOrder: (columnKey: string, newOrder: OrderValue) => void;
  customColumnsToStart?: CustomColumnsGenerator;
  customColumnsToEnd?: CustomColumnsGenerator;
  refetchRowCallback?: (rowHash: string) => void;
  columnLabelOverrides?: ColumnLabelOverrides;
}

export default withLoaderHOC(TableDataDesktop);
